import { api } from './api';
import type {
  FeedbackMaster,
  FeedbackMasterBody,
  FeedbackMasterListParams,
  FeedbackMasterListResult,
} from '../types/feedback-master.types';

/**
 * Feedback Master — aligned to app/api/v1/routers/feedback_master_router.py
 * (prefix /api/v1/feedback-masters).
 *
 * The definition is the only feedback surface: there is no submission or
 * dashboard endpoint to call.
 */
const BASE = '/api/v1/feedback-masters';

interface Envelope<T> {
  success: boolean;
  code?: number;
  message: string;
  data: T;
  pagination?: { totalItems?: number; total?: number; page?: number; page_size?: number };
}

export const feedbackMasterService = {
  async list(params: FeedbackMasterListParams = {}): Promise<FeedbackMasterListResult> {
    const res = await api.get<Envelope<FeedbackMaster[] | { items?: FeedbackMaster[] }>>(
      `${BASE}/list`, { params },
    );
    const raw = res.data?.data as unknown;
    const items = Array.isArray(raw)
      ? (raw as FeedbackMaster[])
      : ((raw as { items?: FeedbackMaster[] })?.items ?? []);
    const p = res.data?.pagination;
    return { items, total: p?.totalItems ?? p?.total ?? items.length };
  },

  async get(id: string, includeDeleted = false): Promise<FeedbackMaster> {
    const res = await api.get<Envelope<FeedbackMaster>>(`${BASE}/${id}`, {
      params: { include_deleted: includeDeleted || undefined },
    });
    return res.data.data;
  },

  async create(body: FeedbackMasterBody): Promise<FeedbackMaster> {
    const res = await api.post<Envelope<FeedbackMaster>>(`${BASE}/create`, body);
    return res.data.data;
  },

  /**
   * Neither `key` nor `appId` is ever sent: both are immutable after create. The
   * service drops appId server-side too, but sending it would imply a re-scope the
   * API does not perform.
   */
  async update(id: string, body: Partial<FeedbackMasterBody>): Promise<FeedbackMaster> {
    const rest = { ...body } as Partial<FeedbackMasterBody> & { key?: string; appId?: string | null };
    delete rest.key;
    delete rest.appId;
    const res = await api.put<Envelope<FeedbackMaster>>(`${BASE}/${id}`, rest);
    return res.data.data;
  },

  async setActive(id: string, isActive: boolean): Promise<FeedbackMaster> {
    const res = await api.patch<Envelope<FeedbackMaster>>(`${BASE}/${id}/active`, { isActive });
    return res.data.data;
  },

  async restore(id: string): Promise<FeedbackMaster> {
    const res = await api.patch<Envelope<FeedbackMaster>>(`${BASE}/${id}/restore`, {});
    return res.data.data;
  },

  /** Soft delete — the key becomes reusable afterwards. */
  async remove(id: string): Promise<void> {
    await api.delete(`${BASE}/${id}`);
  },

  /** What a widget would call: the most specific ACTIVE definition for a component. */
  async resolve(componentType: string, key?: string, componentCode?: string): Promise<FeedbackMaster & { isDefault: boolean }> {
    const res = await api.get<Envelope<FeedbackMaster & { isDefault: boolean }>>(`${BASE}/resolve`, {
      params: { componentType, key, componentCode },
    });
    return res.data.data;
  },
};

export default feedbackMasterService;
